<template>
  <div
    class="mdt-checkbox"
    :class="[{ checked }, { disabled }, { 'has-error': clientErrors.length }]">
    <input
      :id="id"
      ref="checkbox"
      type="checkbox"
      class="labeled"
      :data-test="camelToKebabCase(testKey)"
      :checked="checked"
      :disabled="disabled"
      @change="onChange">
    <div class="state">
      <slot name="icon">
        <i class="fas fa-check icon" />
      </slot>
      <label :for="id" :data-test="`${camelToKebabCase(testKey)}-label`">
        <slot />
        <i
          v-if="tooltip"
          v-tooltip="tooltip"
          class="far fa-info-circle info-icon" />
      </label>
    </div>
    <div
      v-if="clientErrors.length"
      class="input-errors">
      <span class="client-errors">
        {{ clientErrors.join('\n') }}
      </span>
    </div>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { helpers } from '@/utility';

export default {
  name: 'MdtCheckbox',
  mixins: [validationMixin],
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    id: {
      type: [String, Number],
      default: () => helpers.generateUniqueId(),
    },
    checked: {
      type: Boolean,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      default: '',
    },
    testKey: {
      type: String,
      default: '',
    },
  },
  computed: {
    clientErrors() {
      const errors = [];
      if (this.$v.$error) {
        errors.push(this.$options.filters.translate('general_field_is_required'));
      }
      return errors;
    },
  },
  validations() {
    return {
      checked: {
        required: () => !!this.checked,
      },
    };
  },
  methods: {
    setTouched() {
      this.$v.checked.$touch();
    },
    isValid() {
      if (!this.required) return true;
      this.setTouched();
      return !this.$v.checked.$error;
    },
    onChange(e) {
      const { checked } = e.target;
      this.$emit('change', checked);

      // emit mdtDataChanged event so changes could be detected
      this.$emit('mdtDataChanged');
    },
  },
};
</script>

<style lang="scss" scoped>
.mdt-checkbox {
  position: relative;
  display: inline-block;
  color: $color-text-secondary;
  font-size: 16px;
  line-height: calc(1em + 2px);

  &.checked {
    color: $color-text-primary;

    .icon {
      z-index: 1;
    }
  }

  &.disabled {
    opacity: 0.25;

    & > input {
      cursor: not-allowed;
    }
  }

  & > input {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 18px;
    z-index: 2;
    opacity: 0;
    cursor: pointer;

    &:checked ~ .state label:before {
      border-color: $color-theme;
    }

    &:checked ~ .state label:after {
      background-color: $color-theme;
      border-color: $color-theme;
    }

    &:checked ~ .state .icon {
      opacity: 1;
    }

    input:hover ~ .state label:before,
    input:hover ~ .state label:after {
      border-color: $color-text-primary;
    }

    &:hover:not(:checked) ~ .state label:after {
      border-color: $color-text-secondary;
    }
  }

  .state {
    label {
      position: initial;
      display: inline-block;
      min-width: calc(16px + 2px);
      font-size: 16px;
      margin-left: 24px;
      line-height: 20px;
      cursor: pointer;

      ::v-deep > div {
        display: inline;
      }

      &:before,
      &:after {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        width: calc(16px + 2px);
        height: calc(16px + 2px);
        background-color: transparent;
        border: 2px solid transparent;
        border-radius: 20%;
        z-index: 0;
      }

      &:before {
        border-color: $border-color;
      }
      &:hover:before,
      &:hover:after {
        border-color: $color-text-secondary;
      }

      .info-icon {
        margin-left: 4px;
      }
    }

    .icon {
      opacity: 0;
      position: absolute;
      top: 0;
      left: 0;
      width: calc(16px + 2px);
      height: calc(16px + 2px);
      line-height: calc(1em + 2px);
      transform: scale(0.8);
      text-align: center;
      color: $color-white;
      font-size: 16px;
      cursor: pointer;

      &:hover {
        border-color: $color-text-secondary;
      }
    }
  }
}

.input-errors {
  padding-top: 4px;
  font-size: 12px;
  font-weight: $font-weight-normal;
  color: $color-danger;
  white-space: pre-line;
}

.has-error {
  color: $color-danger;
}
</style>
